import React, { useEffect, useState } from "react";
import {
  useGetCustomAgentDetailQuery,
  usePatchCustomAgentMutation,
} from "../../../../app/api/console/customAgentsApiSlice";
import { useGetWebsitesListQuery } from "../../../../app/api/platform/platformWebsitesApiSlice";
import SkillsTab from "./SkillsTab";
import MarkdownField from "./MarkdownField";

const TABS = [
  { id: "settings", label: "Settings" },
  { id: "skills", label: "Skills & subskills" },
  { id: "launch", label: "Launch prompt" },
];

function AgentDetail({ env, slug }) {
  const [tab, setTab] = useState("settings");
  const { data, isLoading, isFetching, refetch } = useGetCustomAgentDetailQuery(
    { env, slug },
    { skip: !slug }
  );
  const [patchAgent, { isLoading: isSaving }] = usePatchCustomAgentMutation();
  const { data: websites = [] } = useGetWebsitesListQuery({
    useProd: env === "prod",
  });

  const agent = data?.agent;
  const skills = data?.skills || [];

  const [form, setForm] = useState(null);
  const [websiteIds, setWebsiteIds] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState(null);
  const [websiteFilter, setWebsiteFilter] = useState("");

  useEffect(() => {
    if (!agent) return;
    setForm({
      title: agent.title || "",
      description: agent.description || "",
      how_it_works: agent.how_it_works || "",
      launch_prompt: agent.launch_prompt || "",
      requires_scope: agent.requires_scope ?? true,
      launch_input_label: agent.launch_input_label || "",
      launch_input_placeholder: agent.launch_input_placeholder || "",
      runtime_agent_id: agent.runtime_agent_id || "",
    });
    setWebsiteIds((agent.websites || []).map((w) => w.website_id));
    setDirty(false);
  }, [agent]);

  const handleSave = async () => {
    if (!form) return;
    try {
      await patchAgent({
        env,
        slug,
        updates: {
          ...form,
          runtime_agent_id: form.runtime_agent_id || null,
          launch_input_label: form.launch_input_label || null,
          launch_input_placeholder: form.launch_input_placeholder || null,
          website_ids: websiteIds,
        },
      }).unwrap();
      setDirty(false);
      setToast("Saved");
      setTimeout(() => setToast(null), 2000);
    } catch (err) {
      setToast(err?.data?.detail || "Save failed");
      setTimeout(() => setToast(null), 4000);
    }
  };

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-purple border-t-transparent" />
      </div>
    );
  }

  const filteredWebsites = (websites || [])
    .filter((w) => {
      const q = websiteFilter.toLowerCase();
      if (!q) return true;
      return (
        (w.website_name || "").toLowerCase().includes(q) ||
        (w.website_id || "").toLowerCase().includes(q) ||
        (w.website_url || "").toLowerCase().includes(q)
      );
    })
    .slice(0, 80);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-body-lg font-semibold text-text-primary truncate">
              {agent.title}
            </h2>
            <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-800">
              Just for you
            </span>
            {isFetching && (
              <span className="text-caption text-text-tertiary">Refreshing…</span>
            )}
          </div>
          <p className="text-body-xs text-text-tertiary mt-0.5 font-mono">{agent.slug}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {toast && (
            <span className="text-body-xs text-text-secondary">{toast}</span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || isSaving}
            className="px-4 py-2 bg-button-primary text-white rounded-xl text-body-xs font-semibold hover:bg-button-primary-hover disabled:opacity-40"
          >
            {isSaving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border-subtle">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-body-xs font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "border-accent-purple text-text-primary"
                : "border-transparent text-text-tertiary hover:text-text-secondary"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "settings" && (
        <div className="space-y-4 max-w-3xl">
          <div>
            <label className="block text-body-xs font-medium text-text-secondary mb-1">
              Title
            </label>
            <input
              value={form.title}
              onChange={(e) => {
                setForm({ ...form, title: e.target.value });
                setDirty(true);
              }}
              className="w-full px-3 py-2.5 border border-border-subtle rounded-xl text-body-sm focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
            />
          </div>
          <MarkdownField
            label="Description"
            value={form.description}
            onChange={(v) => {
              setForm({ ...form, description: v });
              setDirty(true);
            }}
            rows={4}
            hint="Shown on the Hub agent card. Supports light markdown."
          />
          <MarkdownField
            label="How it works"
            value={form.how_it_works}
            onChange={(v) => {
              setForm({ ...form, how_it_works: v });
              setDirty(true);
            }}
            rows={6}
            hint="Customer-facing explanation (often markdown with lists / emphasis)."
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-body-xs font-medium text-text-secondary mb-1">
                Runtime agent id
              </label>
              <input
                value={form.runtime_agent_id}
                onChange={(e) => {
                  setForm({ ...form, runtime_agent_id: e.target.value });
                  setDirty(true);
                }}
                placeholder="(empty = orchestrator + custom skill)"
                className="w-full px-3 py-2.5 border border-border-subtle rounded-xl text-body-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.requires_scope}
                  onChange={(e) => {
                    setForm({ ...form, requires_scope: e.target.checked });
                    setDirty(true);
                  }}
                  className="rounded border-border-default text-accent-purple focus:ring-accent-purple/30"
                />
                <span className="text-body-xs font-medium text-text-secondary">
                  Requires scope
                </span>
              </label>
            </div>
            <div>
              <label className="block text-body-xs font-medium text-text-secondary mb-1">
                Launch input label
              </label>
              <input
                value={form.launch_input_label}
                onChange={(e) => {
                  setForm({ ...form, launch_input_label: e.target.value });
                  setDirty(true);
                }}
                className="w-full px-3 py-2.5 border border-border-subtle rounded-xl text-body-sm focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
              />
            </div>
            <div>
              <label className="block text-body-xs font-medium text-text-secondary mb-1">
                Launch input placeholder
              </label>
              <input
                value={form.launch_input_placeholder}
                onChange={(e) => {
                  setForm({ ...form, launch_input_placeholder: e.target.value });
                  setDirty(true);
                }}
                className="w-full px-3 py-2.5 border border-border-subtle rounded-xl text-body-sm focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
              />
            </div>
          </div>

          <div>
            <label className="block text-body-xs font-medium text-text-secondary mb-1">
              Enabled websites ({websiteIds.length})
            </label>
            <input
              value={websiteFilter}
              onChange={(e) => setWebsiteFilter(e.target.value)}
              placeholder="Filter websites…"
              className="w-full mb-2 px-3 py-2 border border-border-subtle rounded-xl text-body-xs focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
            />
            <div className="max-h-48 overflow-y-auto border border-border-subtle rounded-xl divide-y divide-border-subtle">
              {filteredWebsites.map((w) => {
                const checked = websiteIds.includes(w.website_id);
                return (
                  <label
                    key={w.website_id}
                    className="flex items-center gap-2 px-3 py-2 text-body-xs hover:bg-surface-neutral cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setWebsiteIds((prev) =>
                          checked
                            ? prev.filter((id) => id !== w.website_id)
                            : [...prev, w.website_id]
                        );
                        setDirty(true);
                      }}
                    />
                    <span className="font-medium text-text-primary truncate">
                      {w.website_name || w.website_id}
                    </span>
                    <span className="text-text-tertiary font-mono truncate">
                      {w.website_id}
                    </span>
                  </label>
                );
              })}
              {!filteredWebsites.length && (
                <div className="px-3 py-4 text-text-tertiary text-center">
                  No websites match
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "launch" && (
        <MarkdownField
          label="Launch prompt"
          value={form.launch_prompt}
          onChange={(v) => {
            setForm({ ...form, launch_prompt: v });
            setDirty(true);
          }}
          rows={22}
          mono
          minHeightClass="min-h-[420px]"
          hint={
            <>
              Prompt sent to the orchestrator when this agent runs (ignored when a
              runtime agent id is set). Reference skills as{" "}
              <code className="text-[11px]">custom/skill_name</code>.
            </>
          }
        />
      )}

      {tab === "skills" && (
        <SkillsTab
          env={env}
          slug={slug}
          skills={skills}
          onRefresh={() => refetch()}
        />
      )}
    </div>
  );
}

export default AgentDetail;
